import React from 'react'
import _ from 'lodash'
import moment from 'moment'
import { toastr } from 'react-redux-toastr'
import { SquarePlay, FileText, Image } from 'lucide-react'
import { BooleanCell, FileCell, StatusTag } from '../DataTable'
import ObjectEditor from './editors/ObjectEditor'
import ObjectArrayEditor from './editors/ObjectArrayEditor'
import PhoneNumberEditor from './editors/PhoneNumberEditor'
import { translateName } from '../../utils/i18n'
import getFormValidations from '../../apps/KpModule/components/Form/formValidations'
import { LOCALE_MAPPING } from '../../apps/KpModule/components/Form/Number'

// ── Formatting ────────────────────────────────────────────────────────────────

export const getFormattedAmount = (amount, numberOfDecimals, language) => {
    return amount == null ? '---' :
        new Intl.NumberFormat(LOCALE_MAPPING[language], {
            minimumFractionDigits: numberOfDecimals,
            maximumFractionDigits: numberOfDecimals
        }).format(amount)
}

// ── Display utils ─────────────────────────────────────────────────────────────

export const cellDataGetter = ({ field, t, language }) => {
    const getTranslation = rawValue => {
        if (field.translateName) return translateName(rawValue, language)
        if (field.translate) return t(rawValue, { capitalize: true })
        return rawValue
    }
    switch (field.type) {
        case 'status':
            return ({ rowData }) => {
                const object = _.get(rowData, field.path)
                if (!object) return { value: '' }
                const value = object[field.display]
                return {
                    style: object.style,
                    bStyle: value ? object.bStyle : 'none',
                    value: getTranslation(value)
                }
            }
        case 'date':
            return ({ rowData, dataKey }) => {
                const value = _.get(rowData, dataKey)
                if (!value) return value
                const m = moment(value)
                return m.isValid() ? m.format('YYYY-MM-DD') : value
            }
        default:
            return ({ rowData, dataKey }) => _.get(rowData, dataKey)
    }
}

export const getCellFormatter = (column, { language, t }) => {
    const getTranslation = rawValue => {
        if (column.translateName) return translateName(rawValue, language)
        if (column.translate) return t(rawValue, { capitalize: true })
        return rawValue
    }
    switch (column.type) {
        case 'object':
            return p => column.display ? p.value && getTranslation(p.value[column.display]) : p.value
        case 'objectArray':
        case 'tags':
            return p => {
                const values = p.value
                return values && _.isArray(values) &&
                    values.map(object => getTranslation(_.get(object, column.display))).join(', ')
            }
        case 'text':
        case 'textarea':
            return p => getTranslation(p.value)
        default:
            return undefined
    }
}

export const cellRenderer = (column, { language, groupModel, accessId }) => {
    switch (column.type) {
        case 'boolean':
            return ({ value }) => <BooleanCell value={value} />
        case 'status':
            return ({ value }) => <StatusTag value={value.value} bStyle={value.bStyle} style={value.style} />
        case 'icon':
            return ({ value }) => {
                if (!value) return null
                if (value === 'videoIcon') return <SquarePlay size={16} />
                if (value === 'imageIcon') return <Image size={16} />
                if (value === 'documentIcon') return <FileText size={16} />
                return null
            }
        case 'videoIcon':
            return ({ value }) => value ? <SquarePlay size={16} /> : null
        case 'documentIcon':
            return ({ value }) => value ? <FileText size={16} /> : null
        case 'creatableTags':
            return ({ value }) => (
                <div style={{ display: 'flex', flexDirection: 'row' }}>
                    {value && value.map(v => (
                        <div style={{
                            padding: '3px',
                            margin: '3px',
                            borderRadius: '4px',
                            background: '#addbff91',
                            border: '1px solid #96bedc'
                        }}>
                            {v}
                        </div>
                    ))}
                </div>
            )
        case 'number':
            return ({ value }) => (
                <div style={{ textAlign: 'end', paddingRight: '10px' }}>
                    {getFormattedAmount(value, column.numberOfDecimals, language)}
                </div>
            )
        case 'fileLink':
            return ({ data }) => {
                const cellData = _.get(data, column.path)
                return cellData ? <FileCell file={cellData} language={language} groupModel={groupModel} accessId={accessId} /> : null
            }
        default:
            return null
    }
}

// ── Edit utils ────────────────────────────────────────────────────────────────

export const getCellEditor = (column) => {
    switch (column.type) {
        case 'textarea':   return 'agLargeTextCellEditor'
        case 'object':     return ObjectEditor
        case 'objectArray':return ObjectArrayEditor
        case 'phoneNumber':return PhoneNumberEditor
        default:           return undefined
    }
}

export const getCellEditorParams = (column, { language, t, getDataList }) => {
    switch (column.type) {
        case 'textarea':
            return { maxLength: 999, rows: 5, cols: 100 }
        case 'object':
        case 'objectArray':
        case 'tags':
            return {
                values: getDataList(column.dataList),
                displayKey: column.display,
                width: column.width,
                t
            }
        case 'phoneNumber':
            return { width: column.width, language }
        default:
            return undefined
    }
}

export const getDataList = (id, dataLists) => {
    const dataList = dataLists.byId[id]
    return dataList.list.allIds.map(id => dataList.list.byId[id])
}

// ── Column builder ────────────────────────────────────────────────────────────

export const enhanceColumns = (columns, { t, language, groupModel, accessId, sortBy, sortDirection, dataLists }) => {
    const getDataListFn = id => getDataList(id, dataLists)

    return columns
        .filter(column => !column.hidden)
        .map(column => {
            const getter = cellDataGetter({ field: column, language, t })

            const editionProps = column.editable ? {
                cellEditor: getCellEditor(column),
                cellEditorParams: getCellEditorParams(column, { language, t, getDataList: getDataListFn }),
                cellEditorPopup: ['tags', 'objectArray', 'object', 'textarea', 'phoneNumber'].includes(column.type) || undefined
            } : {}

            const renderer = cellRenderer(column, { language, groupModel, accessId })

            console.log('column.flexGrow', column.flexGrow)

            return {
                ...column,
                dataKey: column.path,
                headerName: t(column.label || column.tKey),
                cellDataType: column.type,
                minWidth: column.width,
                flex: column.flexGrow,
                filter: true,
                sortable: true,
                editable: column.editable,
                sort: sortBy === column.path ? sortDirection?.toLowerCase() : undefined,
                valueGetter: column.dataGetter
                    ? (p) => column.dataGetter(p.data)
                    : (p) => getter({ rowData: p.data, dataKey: column.path }),
                // needed by AccordionComponent's applyFilter / applySort
                cellDataGetter: getter,
                dataGetter: (object) => getter({ rowData: object, dataKey: column.path }),
                ...(column.type === 'status' && {
                    filterValueGetter: (p) => {
                        const v = column.dataGetter ? column.dataGetter(p.data) : getter({ rowData: p.data, dataKey: column.path })
                        return v && v.value
                    }
                }),
                valueSetter: (params) => {
                    const { newValue, data } = params
                    const validations = getFormValidations(column, t, columns, data)
                    for (const validate of validations) {
                        const error = validate(newValue, data, { t })
                        if (error) {
                            setTimeout(() => {
                                toastr.error(t('validationError'), t(error))
                            }, 0)
                            return false
                        }
                    }
                    params.data[params.colDef.dataKey] = params.newValue
                    return true
                },
                valueFormatter: getCellFormatter(column, { language, t }),
                ...(renderer && { cellRenderer: renderer }),
                ...editionProps
            }
        })
}